import { ExamStatus, ExamStatusUtil } from "@/api/enums/exam-status.enum";
import { ExamType } from "@/api/enums/ExamType";
import { ResultsTab } from "@/api/exam/dto/results-tab.enum";
import { ValidationStatus } from "@/api/enums/ValidationStatus";
import { Input } from "@/components/atoms/Input";
import { ComboboxField } from "@/components/molecules/Combobox/Combobox";
import usePersistent from "@/hooks/use-persistent";
import { Perimeter } from "@/lib/perimeter";
import { useState } from "react";
import { useDebounce } from "use-debounce";
import { useSessionContext } from "../session-context";
import { SessionTab } from "../session-tab.enum";
import CandidatesFilterTabs from "./CandidatesFilterTabs/CandidatesFilterTabs";
import CandidatesTable from "./CandidatesTable/CandidatesTable";
import ImportEdcResultsDialog from "./ImportEdcResultsDialog";
import ImportQcmResultsDialog from "./ImportQcmResultsDialog";
import InfoCards from "./InfoCards";
import PerimetersFilterSheet from "./PerimetersFilterSheet";
import RemovePreRegisteredDialog from "./RemovePreRegisteredDialog/RemovePreRegisteredDialog";
import ResultsFilterTabs from "./ResultsFilterTabs/ResultsFilterTabs";

const Candidates = () => {
  const [searchText, setSearchText] = usePersistent<string>(
    "admin-session-candidates-search-text",
    "",
  );
  const [debouncedSearchText] = useDebounce(searchText, 300);
  const { exam, tab } = useSessionContext();
  const [perimeters, setPerimeters] = useState<string[]>([]);
  const [complete, setComplete] = useState<boolean | undefined>(undefined);
  const [formationStatus, setFormationStatus] = usePersistent<
    ValidationStatus | "all"
  >("admin-session-candidates-formation-status", "all");

  const [resultsTab, setResultsTab] = usePersistent<ResultsTab>(
    "admin-session-results-tab",
    ResultsTab.All,
  );

  return (
    <>
      <div className="p-6 flex flex-col gap-4 w-full h-full">
        <InfoCards />
        {tab === SessionTab.Candidates && (
          <CandidatesFilterTabs setComplete={setComplete} />
        )}
        {tab === SessionTab.Results && (
          <ResultsFilterTabs setTab={setResultsTab} tab={resultsTab} />
        )}
        {tab === SessionTab.Candidates && (
          <ComboboxField
            options={[
              { value: "all", label: "Attest. de form. : Toutes" },
              {
                value: ValidationStatus.UNAVAILABLE,
                label: "Non disponible",
              },
              {
                value: ValidationStatus.PENDING,
                label: "À valider",
              },
              {
                value: ValidationStatus.VALIDATED,
                label: "Validé",
              },
              {
                value: ValidationStatus.REFUSED,
                label: "Refusé",
              },
            ]}
            placeholder="Attest. de form. : Toutes"
            value={formationStatus ?? ""}
            onChange={(v) =>
              setFormationStatus(
                v && v !== "all" ? (v as ValidationStatus) : "all",
              )
            }
          />
        )}
        <div className="flex flex-row gap-3">
          <Input
            className="shadow-none"
            id="shadcn-input"
            searchIcon
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value as string)}
            placeholder="Rechercher"
          />

          {ExamStatusUtil.order(exam.status) <=
            ExamStatusUtil.order(ExamStatus.QCMS_GENERATED) && (
            <RemovePreRegisteredDialog />
          )}

          {ExamStatusUtil.order(exam.status) >=
            ExamStatusUtil.order(ExamStatus.QCMS_GENERATED) && (
            <PerimetersFilterSheet
              examId={exam.id}
              perimeters={perimeters}
              setPerimeters={setPerimeters}
            />
          )}

          {/* Renewing exams don't include EDCs*/}
          {tab === SessionTab.Results && exam.type === ExamType.INITIAL && (
            <ImportEdcResultsDialog />
          )}
          {tab === SessionTab.Results &&
            ExamStatusUtil.order(exam.status) >=
              ExamStatusUtil.order(ExamStatus.QCMS_GENERATED) && (
              <ImportQcmResultsDialog />
            )}
        </div>
        <CandidatesTable
          resultsTab={resultsTab}
          perimeters={perimeters.map(Perimeter.toDto)}
          searchText={debouncedSearchText}
          complete={complete}
          formationCertificatesStatus={
            formationStatus === "all" ? undefined : formationStatus
          }
        />
      </div>
    </>
  );
};

export default Candidates;
